import React, { useEffect, useMemo, useRef } from "react";
import type { LeafletMouseEvent } from "leaflet";
import L from "leaflet";
import wkx from "wkx";
import * as TurfHelpers from "@turf/helpers";

import type { Feature, Geometry } from "geojson";

import type { Segment } from "src/types";

interface Props {
  map: L.Map;
  segments: Segment[];
  selected: Segment["segmentId"][];
  onSelect: (segment: Segment, event: LeafletMouseEvent) => void;
  disableClicks?: boolean;
}

const segmentStyle: { [key: string]: L.PathOptions | L.StyleFunction<unknown> } = {
  default: { color: "#40abff", weight: 4 },
  hover: {
    color: "tomato",
    weight: 6,
    lineCap: "butt",
    opacity: 0.6,
  },
  selected: { color: "tomato", weight: 4 },
};

export const SegmentsOnMap = React.memo(function SegmentGeoJSON(props: Props) {
  const { map, segments, selected, onSelect, disableClicks } = props;
  const layersRef = useRef<Map<number, L.GeoJSON>>(new Map());
  const clickHandlersRef = useRef<Map<number, (event: LeafletMouseEvent) => void>>(new Map());
  const rendererRef = useRef<L.Canvas | null>(null);

  const segmentsMap = useMemo(() => {
    return new Map(segments.map((segment) => [segment.segmentId, segment]));
  }, [segments]);

  const preparedSegments = useMemo(() => {
    return prepareSegments(segments);
  }, [segments]);

  useEffect(() => {
    const paneKey = "scenario-segments";

    let pane = map.getPane(paneKey);
    if (!pane) pane = map.createPane(paneKey);
    pane.style.zIndex = "500";

    if (!rendererRef.current) {
      rendererRef.current = L.canvas({ tolerance: 5, pane: paneKey });
    }

    function handleEachFeature(feature: Feature<Geometry, Record<string, unknown>>, layer: L.GeoJSON) {
      const segmentId = feature.properties.id;
      if (typeof segmentId !== "number") return;
      layersRef.current.set(segmentId, layer);
      const isSelected = selected.includes(segmentId);
      if (isSelected) layer.setStyle(segmentStyle.selected);
      const clickHandler = (event: LeafletMouseEvent) => {
        const segment = segmentsMap.get(segmentId);
        if (segment) onSelect(segment, event);
      };
      clickHandlersRef.current.set(segmentId, clickHandler);
      if (!disableClicks) {
        layer.on("click", clickHandler);
      }
      layer.on("mouseover", (event: LeafletMouseEvent) => {
        if (isSelected) layer.setStyle({ ...segmentStyle.selected });
        else layer.setStyle(segmentStyle.hover);
      });
      layer.on("mouseout", (event: LeafletMouseEvent) => {
        if (isSelected) layer.setStyle(segmentStyle.selected);
        else layer.setStyle(segmentStyle.default);
      });
    }

    const geosjson = L.geoJSON(preparedSegments, {
      // @ts-ignore: missing type and documentation. Issue made: https://github.com/Leaflet/Leaflet/issues/8289
      renderer: rendererRef.current,
      bubblingMouseEvents: false,
      onEachFeature: handleEachFeature,
      style: {
        color: "#40abff",
      },
    }).addTo(map);

    // Store refs in local variables for cleanup
    const layers = layersRef.current;
    const clickHandlers = clickHandlersRef.current;

    return function cleanup() {
      geosjson.remove();
      layers.clear();
      clickHandlers.clear();
    };
  }, [preparedSegments, map, selected, segmentsMap, onSelect, disableClicks]);

  // Manage click listeners based on disableClicks prop
  useEffect(() => {
    const isDisabled = disableClicks ?? false;
    const layers = layersRef.current;
    const clickHandlers = clickHandlersRef.current;

    layers.forEach((layer, segmentId) => {
      const clickHandler = clickHandlers.get(segmentId);
      if (!clickHandler) return;

      if (isDisabled) {
        // Remove click listener
        layer.off("click", clickHandler);
      } else {
        // Re-add click listener
        layer.on("click", clickHandler);
      }
    });
  }, [disableClicks]);

  return null;
});

function prepareSegments(segments: Segment[]) {
  const features = segments.map((segment) => {
    const geometry = wkx.Geometry.parse(segment.lineString_frontendSrid);
    const feature = TurfHelpers.feature(geometry.toGeoJSON(), {
      id: segment.segmentId,
    });
    return feature;
  });

  return TurfHelpers.featureCollection(features);
}
